<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from '@/components/Dialog/index.vue'
import Address from '@/components/Address/index.vue'
import { useUserStore } from '@/stores/UserStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { addressValidation } from '@/helpers/validation'
import { useRoute } from 'vue-router'
import { getPlanInfo } from '@/helpers/general'
const route = useRoute()

const userStore = useUserStore()
const billingStore = useBillingStore()
const globalStore = useGlobalStore()
const configStore = useConfigStore()

const user = computed(() => userStore?.user || {})
const location = ref({
  city: user.value.city,
  state: user.value.state,
  country: user.value.country,
  postal_code: user.value.postal_code,
  address_1: user.value.address_1,
  address_2: user.value.address_2,
})
const currentPlan = ref(null)
const errors = ref([])

const closeDialog = (setValidAddressToDefault = true) => {
  globalStore.showAddressDialog = false
  userStore.validAddress = setValidAddressToDefault

  if (route.path.includes('account-creation')) {
    userStore.fetchUserTaxInfo()
  }
}

const submitDialog = async () => {
  errors.value = addressValidation(location.value)

  if (errors.value.length > 0 || !userStore.validAddress) return
  
  await userStore.updateUser(location.value) 

  globalStore.showAddressBanner = false

  currentPlan.value?.id && billingStore.updateSubscription({
    plan_id: currentPlan.value?.id,
    taxRate: userStore.tax?.taxRate
  })

  closeDialog(true)
}

watch([location], async () => {
  let validAddress = false

  if (location.value.city && location.value.state && location.value.country && location.value.address_1 && location.value.postal_code) {
    validAddress = await userStore.validationAddress(location.value)
    userStore.validAddress = validAddress
  }

  if (validAddress) {
    currentPlan.value = getPlanInfo() 
    userStore.selectedPlanPrice = configStore.config.subscription?.annual ? currentPlan.value.price.annual_final : currentPlan.value.price.monthly
    userStore.fetchUserTaxInfo(location.value)
  }
}, { deep: true })

watch(() => userStore.tax, () => {
  globalStore.showPlanInfoDialog = true
})
</script>

<template>
  <Dialog 
    @closeDialog="closeDialog"
    @submitDialog="submitDialog"
    :openDialogField="'showAddressDialog'"
    :warningBlock="!userStore.validAddress"
  >
    <template v-slot:defaultHeader>
      <span>Add Address</span>
    </template>
    <template v-slot:warningBlock>
      <p class="text-xs text-red-500 mt-2" v-if="!userStore.validAddress">
        {{ globalStore.addressWarningMessage }}
      </p>
    </template>
    <template v-slot:dialogBody>
      <Address
        class="mb-4"
        :location="location"
        :errors="errors"
      />

      <section v-if="globalStore.showPlanInfoDialog && currentPlan && userStore.validAddress" class="mb-4">
        <ul class="flex flex-col gap-y-1.5 child:flex child:items-center child:justify-between text-xs">
          <li>
            <span class="text-gray-500">{{ currentPlan.name }} Plan (Current Plan)</span>
            <span>{{ '$' + (configStore.config.subscription?.annual ? currentPlan.price.annual_final : currentPlan.price.monthly) }}/{{ configStore.config.subscription?.annual ? 'year' : 'month' }}</span>
          </li>
          <li v-if="userStore.user?.country === 'US'">
            <span class="text-gray-500">Sales Tax</span>
            <span>{{ `$${userStore.tax.taxAmount?.toFixed(2)}` }}</span>
          </li>
          <li class="!text-sm !font-medium" v-if="userStore.tax?.finalAmount">
            <span>Next payment amount</span>
            <span> ${{ userStore.tax?.finalAmount.toFixed(2) }} </span>
          </li>
        </ul>
      </section>
    </template>
  </Dialog>
</template>
